import useToasterStore from '@/stores/toaster'
import ContractService from '@/services/contract'
import router from '@/router'
import type { ContractItem } from '@/data/schema/contract-item'
import type { ContractItemStatus } from '@/data/schema/contract-item-status'
import type { Contract } from '@/data/schema/contract'
import type { WorkspaceArea } from '@/data/types/area'
import { contractItemStatusMap } from '@/utils/statusMaps'
import type { StatusUpdate } from '@/data/interfaces/status-update'
import type { Vehicle } from '@/data/schema/vehicle'
import type { StatusKey } from '@/data/types/contract-status'
import type { CollectionItem } from '@/data/schema/collection-item'
import { JourneyItem } from '@/data/schema/journey-item'

interface Params {
	withItems?: boolean
	withStockCheckItems?: boolean
	withPickItems?: boolean
	withLoadItems?: boolean
	withContractStatus?: boolean
	withStockCheckStatus?: boolean
	withPickStatus?: boolean
	withLoadStatus?: boolean
	pickRefs?: string | string[]
	deliveryMethod?: string | null
	depot?: string
	operatorId?: string
	loadOperatorId?: string
}

export default function useContracts() {
	const api = new ContractService()
	const toaster = useToasterStore()

	/**
	 * Get contract and contract item data
	 * @param contractNumber string
	 * @param pickRefs string[] String array of pick references
	 * @returns Promise
	 */
	async function fetchData(
		contractNumber: string,
		{
			withItems = true,
			withStockCheckItems = false,
			withPickItems = false,
			withLoadItems = false,
			withContractStatus = false,
			withStockCheckStatus = false,
			withPickStatus = false,
			withLoadStatus = false,
			pickRefs = '',
			deliveryMethod = null,
			depot = '',
			operatorId = '',
			loadOperatorId = ''
		}: Params = {}
	): Promise<{ contract: Contract; items: ContractItem[] }> {
		const params = {
			withItems: withItems ? withItems : undefined,
			withStockCheckItems: withStockCheckItems ? withStockCheckItems : undefined,
			withPickItems: withPickItems ? withPickItems : undefined,
			withLoadItems: withLoadItems ? withLoadItems : undefined,
			withContractStatus: withContractStatus ? withContractStatus : undefined,
			withStockCheckStatus: withStockCheckStatus ? withStockCheckStatus : undefined,
			withPickStatus: withPickStatus ? withPickStatus : undefined,
			withLoadStatus: withLoadStatus ? withLoadStatus : undefined,
			pickRefs: pickRefs ? pickRefs : undefined,
			deliveryMethod: deliveryMethod ? deliveryMethod : undefined,
			depot: depot ? depot : undefined,
			operatorId: operatorId ? operatorId : undefined,
			loadOperatorId: loadOperatorId ? loadOperatorId : undefined
		}

		if (params.pickRefs && Array.isArray(params.pickRefs) && params.pickRefs.length > 0) {
			params.pickRefs = params.pickRefs.join()
		}

		try {
			const res = await api.get(contractNumber, params)
			const contract = res.data.data
			const items = res.data.data.items
			return { contract, items }
		} catch (error: any) {
			if (error.response?.status == 404) {
				router.push({ name: 'not-found' })
			} else {
				toaster.error({
					text: 'An error occurred retrieving contract items'
				})
			}

			throw error
		}
	}

	/**
	 * Get unique picking references from items for dropdown
	 */
	function getPickingRefOptions(items: Array<ContractItem>) {
		const pickingReferences = Array.from(
			new Set(
				items
					.filter((obj) => obj.picking_reference !== '')
					.map((obj) => obj.picking_reference)
					.sort((a, b) => parseInt(a ?? '') - parseInt(b ?? ''))
			)
		)

		return pickingReferences.map((value) => ({ value }))
	}

	/**
	 * Get unique picking references from items for dropdown
	 */
	function getDepotOptions(
		items: Array<ContractItem | CollectionItem | JourneyItem>,
		status: StatusKey
	) {
		const depots = Array.from(
			new Set(
				items.flatMap((item) =>
					item[status] && item[status]!.length > 0
						? item[status]!.map((status) => status.depot).filter(
								(depot): depot is string => !!depot
							)
						: [item.depot]
				)
			)
		)

		return depots.map((value) => ({ value }))
	}

	/**
	 * Get unique delivery methods from items for dropdown
	 */
	function getDeliveryMethodOptions(items: Array<ContractItem>, key: WorkspaceArea) {
		const deliveryMethods = Array.from(
			new Set(
				items
					.filter((obj) => statusParam(obj, 'delivery_method', key))
					.map((obj) => statusParam(obj, 'delivery_method', key))
					.sort((a, b) => parseInt(a ?? '') - parseInt(b ?? ''))
			)
		)

		return deliveryMethods.map((value) => ({ value }))
	}

	/**
	 * Pick: Send Items to Load
	 * @param items
	 * @param delivery_type
	 * @param vehicle
	 */
	function sendToLoad(
		items: ContractItem[],
		delivery_type: string,
		vehicle: Vehicle
	): Promise<any> {
		const saveItems = items.map((item) => ({
			id: item.id,
			load_status: 1,
			status: 7,
			status_id: item.pick_status?.[0].id,
			delivery_type: delivery_type,
			delivery_method: vehicle?.code ?? 'N/A',
			delivery_name: vehicle?.name ?? 'N/A',
			tos: item.pick_status?.[0].tos
		}))

		toaster.working({
			text: 'Sending selected items to load...',
			dismissable: false,
			timeout: 0
		})

		return api
			.bulkUpdateStatus(saveItems as StatusUpdate[], 'pick-status')
			.then(() => {
				toaster.clear()
				toaster.success({ text: 'Items sent successfully!' })
			})
			.catch((error: any) => {
				toaster.clear()
				toaster.error({ text: 'Error occurred sending items' })
				console.error(error)
			})
	}

	return {
		fetchData,
		getPickingRefOptions,
		getDeliveryMethodOptions,
		getDepotOptions,
		sendToLoad
	}
}

/**
 * Get contract item availability data
 * @param item
 */
export async function fetchItemAvailability(item: ContractItem, depot?: string) {
	const toaster = useToasterStore()
	const api = new ContractService()

	if (item.item_number) {
		return await api
			.getStock(item.item_number, depot)
			.then((res) => {
				return res.data.data
			})
			.catch((error) => {
				console.error(error)

				toaster.error({
					text: 'An error occurred retrieving item availability'
				})

				return null
			})
	} else {
		toaster.warning({
			text: 'Invalid item number'
		})
	}
}

/**
 * Lookup a value for a contract item status
 * example: item.stock_check_status[0].status will become:
 * statusParam(item, 'status', 'stock-check')
 * @param item ContractItem
 * @param param string The key param you are looking for e.g. 'status'
 * @param area WorkspaceArea stock-check | pick | load-delivery
 * @param index number Index value (default: 0)
 * @returns any
 */
export function statusParam(
	item: ContractItem,
	param: keyof ContractItemStatus,
	area?: WorkspaceArea,
	index: number = 0
): any {
	let value = null

	if (!item || !param) {
		return null
	}

	if (area) {
		const key = contractItemStatusMap[area] as keyof ContractItem
		const status: ContractItemStatus | null =
			(item[key] as ContractItemStatus[])?.[index] ?? null

		if (status) {
			value = status[param] ?? null
		}

		return value
	}

	return item?.[param as keyof ContractItem] ?? null
}

export function useSendToLoad() {}
